import { LinkTo } from '@ember/routing';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import type { IconName } from '@mylighthouse/prism-ember/types/components/prism/icon';
import { t } from 'ember-intl';
import { and } from 'ember-truth-helpers';
import HotelPricingAssistantDayDetailTabsRecommendationReasonAccordion from 'lumen/components/hotel/pricing-assistant/day-detail/tabs/recommendation-reason-accordion';
import type { Carecommendation } from 'lumen/models';
import type { UserRuleRecommendationReason } from 'lumen/models/pa-recommendation-reason';
import {
  RECOMMENDATION_REASON_TRANSLATION_KEY,
  RECOMMENDATION_REASON_TYPE_USER_RULE,
} from 'lumen/utils/constants/pricing-assistant-constants';

interface FormattedUserRulesReason {
  details: { label: string; route: string }[];
  icon: IconName;
  isIncrease: boolean | null;
  label: string;
}

interface HotelPricingAssistantDayDetailTabsUserRulesRecommendationReasonsSignature {
  Args: {
    carecommendationOfSelectedDay?: Carecommendation;
  };
}

export default class HotelPricingAssistantDayDetailTabsUserRulesRecommendationReasons extends Component<HotelPricingAssistantDayDetailTabsUserRulesRecommendationReasonsSignature> {
  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];

  get reasons(): FormattedUserRulesReason[] {
    const { carecommendationOfSelectedDay } = this.args;

    const reasons =
      carecommendationOfSelectedDay?.paRecommendationReason?.userRulesReasons;

    if (!reasons) {
      return [];
    }

    return reasons.reduce((accumulator, reason) => {
      /*
        Only show reasons for which we have a translation on frontend.
        Currently, recommendations without reasons return a fallback
        translation key, but they are not defined in FE yet.
      */
      if (RECOMMENDATION_REASON_TRANSLATION_KEY[reason.translation_key]) {
        const details = this.getDetails(reason);
        const label = this.getLabel(reason);

        accumulator.push({
          details,
          icon: 'settings',
          isIncrease: reason.is_increase,
          label,
        });
      }

      return accumulator;
    }, [] as FormattedUserRulesReason[]);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  private getDetails(reason: UserRuleRecommendationReason) {
    const defaultValue = {
      label: '',
      route: '',
    };

    return reason.details.map((detail) => {
      const { is_increase, reason_type, rounding_value, translation_key } =
        detail;

      const translationKey =
        RECOMMENDATION_REASON_TRANSLATION_KEY[translation_key];

      if (!translationKey) {
        return defaultValue;
      }

      switch (reason_type) {
        case RECOMMENDATION_REASON_TYPE_USER_RULE.MIN_MAX: {
          const { carecommendationOfSelectedDay } = this.args;

          if (!carecommendationOfSelectedDay) {
            return defaultValue;
          }

          // low/high keys from BE are not directly proportional to min and max settings, but to is_increase
          // (i.e. low/high refers to if a rate needs to be reduced or increased base on the recommendations)
          if (is_increase) {
            const minSettingsPrice = this.hotelAndMarketService.formatRate(
              carecommendationOfSelectedDay.carate.userSetMinimum ?? 0,
            );

            return {
              label: this.intl.t(translationKey, {
                minSettingsPrice,
                htmlSafe: true,
              }),
              route: 'hotel.settings.recommendations.price-range',
            };
          }

          const maxSettingsPrice = this.hotelAndMarketService.formatRate(
            carecommendationOfSelectedDay.carate.userSetMaximum ?? 0,
          );

          return {
            label: this.intl.t(translationKey, {
              maxSettingsPrice,
              htmlSafe: true,
            }),
            route: 'hotel.settings.recommendations.price-range',
          };
        }

        case RECOMMENDATION_REASON_TYPE_USER_RULE.ROUNDING: {
          return {
            label: this.intl.t(translationKey, {
              roundingValue: rounding_value,
            }),
            route: 'hotel.settings.recommendations',
          };
        }

        default: {
          return defaultValue;
        }
      }
    });
  }

  private getLabel(reason: UserRuleRecommendationReason): string {
    const { translation_key } = reason;

    return this.intl.t(RECOMMENDATION_REASON_TRANSLATION_KEY[translation_key]);
  }

  <template>
    {{#each this.reasons as |reason|}}
      <HotelPricingAssistantDayDetailTabsRecommendationReasonAccordion
        @reason={{reason}}
      >
        <ul class="flex list-inside list-disc flex-col gap-200">
          {{#each reason.details as |detail|}}
            <li class="text-200--regular">
              {{detail.label}}
              {{#if
                (and
                  this.access.hasPricingAssistantSettingsEditRights detail.route
                )
              }}
                <LinkTo
                  @route={{detail.route}}
                  class="prism-link"
                  target="_blank"
                >
                  {{t "general.edit"}}
                </LinkTo>
              {{/if}}
            </li>
          {{/each}}
        </ul>
      </HotelPricingAssistantDayDetailTabsRecommendationReasonAccordion>
    {{/each}}
  </template>
}
